export const LEGAL_PDF_PATH = '/legal/localally-terms-and-privacy.pdf';

export interface QuickSummaryItem {
  title: string;
  body: string;
}

export interface LegalSection {
  title: string;
  paragraphs?: string[];
  bullets?: string[];
}

export const quickSummaryItems: QuickSummaryItem[] = [
  {
    title: 'We are a Bridge',
    body: 'LocalAlly connects you with local tutors and sellers. We do not employ tutors or own the products sold.',
  },
  {
    title: 'Safety First',
    body: 'All tutors undergo ID verification, but parents are advised to conduct their own demo sessions.',
  },
  {
    title: 'Your Data is Yours',
    body: 'We only use your location to find matches within 20km. We will never sell your phone number.',
  },
] ;

export const termsSections: LegalSection[] = [
  {
    title: 'Section 1: The Platform Role (Intermediary)',
    paragraphs: [
      'LocalAlly (the "Platform") is an intermediary as defined under Section 79 of the IT Act, 2000. We provide a digital marketplace for Tutors ("Sellers") and Parents/Students ("Buyers").',
      'All service contracts are strictly between the Tutor and the Parent. LocalAlly is not liable for academic results, personal conduct, or product quality.',
    ],
  },
  {
    title: 'Section 2: Tutor & Seller Verification',
    bullets: [
      'Mandatory Disclosure: All Tutors must provide a valid Government ID (Aadhaar/Voter ID) and educational certificates such as B.Ed.',
      'True Representation: You agree that all descriptions, subjects, and distance availability (0-20km) are accurate.',
      'Prohibited Content: You shall not post misleading advertisements or coaching claims that violate CCPA 2024 Guidelines.',
    ],
  },
  {
    title: 'Section 3: Proximity & Location Data',
    bullets: [
      '0-20km Logic: To function, LocalAlly requires access to your GPS data. By using the app, you provide explicit consent for this processing.',
      'Precision: We use this data only to facilitate local discovery. Your exact home address is only shared with a tutor after you confirm a booking.',
    ],
  },
  {
    title: 'Section 4: Payments & Fees',
    bullets: [
      'No-Circumvention: Tutors and Parents agree not to exchange contact details or payments off-platform to bypass service fees. Violation results in a permanent ban.',
      'Escrow: Payments made through LocalAlly are held until the completion of the scheduled session or delivery.',
    ],
  },
  {
    title: 'Section 5: Safety & Code of Conduct',
    bullets: [
      'Child Safety: Tutors must maintain professional decorum. Any report of misconduct involving a minor will result in immediate profile suspension and reporting to the local police (Jubilee Hills/Cyberabad/District Police).',
      'Dispute Resolution: Any disputes arising from services in Hyderabad or its districts shall be subject to the exclusive jurisdiction of the courts in Hyderabad, Telangana.',
    ],
  },
] ;

export const privacySections: LegalSection[] = [
  {
    title: 'What Data We Collect',
    bullets: [
      'Identity and contact: Name, phone number, email, username, role, and account verification status.',
      'Student profile: Class, subjects, curriculum, date of birth, gender, interests, hobbies, and parent-managed registration details where applicable.',
      'Tutor profile: Subjects, classes, languages, experience, profile photo, qualification details, availability, teaching radius, and verification status.',
      'Verification documents: Tutor identity, qualification, and coaching-centre documents when uploaded for review.',
      'Location: Approximate or precise location only when needed for nearby search, profile matching, rides, or service discovery.',
      'App activity: Searches, demo requests, exam attempts, results, certificates, complaints, reviews, subscriptions, and support interactions.',
      'Device and diagnostics: Push token, app version, locale, device platform, crash/error logs, and non-sensitive performance information when enabled.',
      'Payments: Transaction reference, subscription status, wallet/payment state, refund status, and invoice/support details where applicable.',
    ],
  },
  {
    title: 'How We Use Data',
    bullets: [
      'Provide login, account recovery, role-based dashboards, tutoring, exams, books, rides, scholarships, and support services.',
      'Show nearby tutors, shops, rides, or service providers using the minimum location precision required for the feature.',
      'Send requested or account-relevant notifications such as demo requests, exam reminders, result availability, subscription status, payment state, and complaint updates.',
      'Review tutor verification information, handle complaints, prevent abuse, protect minors, and maintain audit trails for safety.',
      'Improve reliability, troubleshoot errors, measure product usage, and verify payment or subscription entitlements.',
    ],
  },
  {
    title: 'Sharing and Processors',
    bullets: [
      'We do not sell phone numbers or verification documents.',
      'Limited data may be shared with payment processors, notification providers, hosting providers, analytics/crash providers, communication providers, or verification/support vendors only as needed to run LocalAlly.',
      'Private tutor verification documents are not shown publicly. Public profiles show only approved profile information and verification status.',
      'Legal, fraud-prevention, safety, or dispute information may be shared with authorities or advisors when required by law or necessary to protect users.',
    ],
  },
  {
    title: 'Children and Student Safety',
    bullets: [
      'LocalAlly supports school students and encourages parent or guardian involvement for younger students.',
      'We avoid publicly exposing a minor student phone number, exact home address, or precise live location.',
      'Tutor verification badges explain review status but do not guarantee personal safety; parents and students should use demo-class safety guidance and reporting tools.',
      'Reports, complaints, blocks, moderation, and audit trails may be used to respond to inappropriate interaction or safety incidents.',
      'We do not use behavioural advertising targeted to children.',
    ],
  },
  {
    title: 'Retention and Security',
    bullets: [
      'Account/profile data is kept while the account is active and then deleted, anonymised, or retained only where required for legal, payment, fraud-prevention, dispute, tax, or safety reasons.',
      'Verification documents are retained only for review, compliance, dispute, or fraud-prevention needs and are not publicly accessible.',
      'Access tokens, OTPs, and payment credentials must not be sent through analytics events or stored in public app assets.',
      'We use HTTPS, role-based access, private document access, and operational audit logs where applicable.',
    ],
  },
  {
    title: 'Your Rights Under the DPDP Act 2023',
    bullets: [
      'Access: Know what data we have about you.',
      'Correction: Fix any wrong information on your profile.',
      'Erasure: Ask us to delete your account and associated data through the in-app Privacy & Account screen or the public account deletion page.',
      'Withdrawal: Turn off optional notification categories and stop using location-based features when you do not want location processed.',
      'Grievance: Contact our Grievance Officer if you have a complaint.',
    ],
  },
  {
    title: 'Account Deletion',
    bullets: [
      'In-app path: Profile or menu, then Privacy & Account, then Delete Account.',
      'Public request path: https://localally.in/account-deletion.',
      'After an in-app deletion request, public profile visibility is disabled promptly and the account can no longer log in.',
      'A reference number is issued for support follow-up.',
    ],
  },
  {
    title: 'Grievance Redressal',
    bullets: [
      'Officer Name: [XXXXXXXX]',
      'Email: support@localally.in',
      'Response Time: We acknowledge complaints within 48 hours and resolve them within 1 month.',
    ],
  },
] ;
